import React, { useCallback, useEffect, useState } from "react";
import { Dropdown } from "react-bootstrap";
import { connect } from "react-redux";
import {
  mapDispatchToPropsManagement,
  mapStateToPropsManagement,
} from "../../../modules/profileManagement";
import { mapStateToPropsLogin } from "../../../modules/login";
import { ProfileCard } from "../../ui-library/ProfileCard/ProfileCard";
import "./AdminUsers.css";
import { profiles } from "../../../modules/constants";
import DefaultTable, {
  ActionButtons,
  Filter,
  Line,
} from "../../ui-library/CustomTable/DefaultTable";
import { withTheme } from "styled-components";
import AccountStatus from "../../library/AccountStatus/AccountStatus";
import { UserStatus } from "../../library/AccountStatus/AccountStatus.constants";
import CustomModal from "../../ui-library/CustomModal/CustomModal";
import SmartAddUserAccessManagement from "../AddUserAccessManagement/AddUserAccessManagement";
import { RegistrationType } from "../../library/AddUserAccessManagement/AddUserAccessManagement.constants";
import TableDropDown from "../../ui-library/TableDropDown/TableDropDown";
import SmartAddSite from "../AddSite/AddSite";
import AccentButton from "../../ui-library/Buttons/AccentButton/AccentButton";
import ConfirmRequest from "../../library/ConfirmRequest/ConfirmRequest";
import { useTranslation } from "react-i18next";
import CustomDropdown from "../../ui-library/CustomDropdown/CustomDropdown";
import { smoothScrollToTop } from "../../../modules/utils";

export const AdminUsers = (props) => {
  const {
    users,
    fetchAdminUsers,
    resetPassword,
    unlockUser,
    blockUnblockUser,
    resetStatus,
    resendInvite,
    canEditNonParticipant,
    theme,
    totalitems,
    searchedParams,
    canBlockNonParticipant,
    canUnlockNonParticipant,
    canResendInviteNonParticipant,
    canCreateStudyAdmin,
    canAddSite,
    studies,
    roles,
    accountUserName,
    loading,
  } = props;

  const { t } = useTranslation();

  const [blockConfirmRequest, setBlockConfirmRequest] = useState(null);
  const [addUserClick, setAddUserClick] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedUsername, setSelectedUsername] = useState(null);
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [showAddSiteModal, setShowAddSiteModal] = useState(false);

  const getData = useCallback(
    (params) => {
      fetchAdminUsers(params).finally(() => {
        if (params && params.limit !== searchedParams.limit) {
          smoothScrollToTop();
        }
      });
    },
    [fetchAdminUsers, searchedParams]
  );

  useEffect(() => {
    if (totalitems === null) {
      getData(searchedParams);
    }
    resetStatus();
  }, [resetStatus, totalitems, getData, searchedParams]);

  const studyInfo = {
    studyId: studies?.studies[0]?.Key,
    studyCode: studies?.studies[0]?.Code,
  };

  const getDropDownItems = (user) => {
    const dropDownMenuItems = {
      UNLOCK_ACCOUNT: {
        text: t("appText.management.unlockAccount"),
        callBack: (user) => {
          unlockUser(user.userName).then(() => {
            getData();
          });
        },
        id: 1,
        isDisabled: user.userName === accountUserName,
        isVisible:
          canUnlockNonParticipant && user.status === UserStatus.LOCKED.id,
      },
      BLOCK_UNBLOCK_USER: {
        text:
          user.status === UserStatus.BLOCKED.id
            ? t("appText.management.unblockUser")
            : t("appText.management.blockUser"),
        callBack: (user) => {
          if (user.status !== UserStatus.BLOCKED.id) {
            setBlockConfirmRequest(user.userName);
          } else {
            blockUnblockUser(user.userName, false).then(() => {
              getData();
            });
          }
        },
        id: 2,
        isDisabled: accountUserName === user.userName,
        isVisible:
          canBlockNonParticipant && user.status !== UserStatus.LOCKED.id,
      },
      ACCESS_MANAGEMENT: {
        text: t("appText.management.accessManagement"),
        callBack: (user) => setSelectedUser(user),
        id: 3,
        isDisabled: accountUserName === user.userName,
        isVisible: canEditNonParticipant,
      },
      RESEND_INVITE: {
        text: t("appText.management.resendInvite"),
        callBack: (user) => resendInvite(user.userName),
        id: 4,
        isDisabled: accountUserName === user.userName,
        isVisible:
          canResendInviteNonParticipant &&
          user.status === UserStatus.INVITED.id,
      },
      RESET_PASSWORD: {
        id: 5,
        text: t("appText.management.resetPassword"),
        isVisible: true,
        isDisabled:
          accountUserName === user.userName ||
          user.status !== UserStatus.ACTIVE.id,
        callBack: (user) => handleResetPassword(user.userName),
      },
    };
    return Object.values(dropDownMenuItems);
  };

  const defaultProps = {
    headers: [
      { label: t("appText.management.role") },
      { label: t("appText.management.site") },
      { label: t("appText.management.name") },
      { label: t("appText.management.email") },
      { label: t("appText.management.accountStatus") },
      { label: t("appText.management.options") },
    ],
    maincolor: theme.success,
    totalitems,
    hasSearch: 1,
    haspagesizefilter: 1,
    haspagination: 1,
    searchedParams,
    loading,
    getData,
  };

  const changeFilterBy = (filter) => {
    const params = {
      ...searchedParams,
      page: 0,
      filter: filter.id,
    };

    getData(params);
  };

  const onCloseAddUserAccessManagement = (refreshData) => {
    setAddUserClick(false);
    setSelectedUser(null);

    if (refreshData) {
      fetchAdminUsers(0, 10, "", null);
    }
  };

  const addNewSite = () => {
    setShowAddSiteModal(true);
  };

  const closeAddNewSiteModal = () => {
    setShowAddSiteModal(false);
    fetchAdminUsers(0, 10, "", null);
  };

  const confirmBlockRequestHandler = () => {
    blockUnblockUser(blockConfirmRequest, true).then(() => {
      getData();
    });

    setBlockConfirmRequest(null);
  };

  const handleResetPassword = (userName) => {
    setSelectedUsername(userName);
    setShowResetPasswordModal(true);
  };

  const confirmResetPassword = (userName) => {
    resetPassword(userName);
    setShowResetPasswordModal(false);
  };

  return (
    <div
      data-testid="admin-users"
      className="mt-5"
      style={{ paddingTop: "2em", position: "relative" }}
    >
      <div className="mb-5">
        <ProfileCard
          title={t("appText.management.header.adminUsers")}
          cardHeader="card-header-separator-green"
          iconClassName="fas fa-user-shield"
        >
          <CustomModal
            title={`${t("appText.management.addNew")} ${
              studyInfo.studyCode
            } ${t("appText.management.site")}`}
            onClose={() => setShowAddSiteModal(false)}
            size="sm"
            show={showAddSiteModal}
            titleSeparatorBg={props.theme?.greenGradient}
          >
            <SmartAddSite
              studyId={studyInfo.studyId}
              onClose={closeAddNewSiteModal}
              SiteButton={AccentButton}
              greenTheme={true}
            />
          </CustomModal>

          <CustomModal
            title={
              selectedUser
                ? t("appText.management.userAccess")
                : t("appText.management.addNewUser")
            }
            onClose={() => onCloseAddUserAccessManagement(false)}
            show={selectedUser !== null || addUserClick}
            titleSeparatorBg={props.theme?.greenGradient}
          >
            <SmartAddUserAccessManagement
              onClose={() => onCloseAddUserAccessManagement(true)}
              registrationType={RegistrationType.Admin}
              selectedUser={selectedUser}
              studies={studies}
              roles={roles?.roles}
            />
          </CustomModal>

          <CustomModal
            title={t("appText.management.areYouSure")}
            onClose={() => setBlockConfirmRequest(false)}
            show={!!blockConfirmRequest}
          >
            <ConfirmRequest
              message={`${t("appText.management.blockUserConfirm")}.`}
              onConfirm={confirmBlockRequestHandler}
              onClose={() => setBlockConfirmRequest(false)}
            />
          </CustomModal>

          <CustomModal
            title={t("appText.management.areYouSure")}
            onClose={() => setShowResetPasswordModal(false)}
            show={!!showResetPasswordModal}
          >
            <ConfirmRequest
              message={t("appText.management.resetPasswordConfirm", {
                userName: selectedUsername,
              })}
              onConfirm={() => confirmResetPassword(selectedUsername)}
              onClose={() => setShowResetPasswordModal(false)}
            />
          </CustomModal>

          <DefaultTable {...{ ...defaultProps }}>
            <Filter>
              <form className="form-inline mb-1 justify-content-start">
                {`${t("appText.management.status")}:`}

                <CustomDropdown
                  className="ml-2"
                  placeholder={UserStatus.ALL.value}
                  values={Object.values(UserStatus)}
                  onChange={(e) => changeFilterBy(e)}
                />
              </form>
            </Filter>
            <ActionButtons>
              {canAddSite && (
                <AccentButton
                  data-testid="add-site-btn"
                  size="sm"
                  className="mr-2"
                  onClick={addNewSite}
                >
                  {t("appText.management.addSite")}
                </AccentButton>
              )}
              {canCreateStudyAdmin && (
                <AccentButton
                  className="mr-2"
                  size="sm"
                  onClick={() => {
                    setAddUserClick(true);
                  }}
                >
                  {t("appText.management.addUser")}
                </AccentButton>
              )}
            </ActionButtons>
            {users.map((user, index) => (
              <Line key={`user-entry-${index}`}>
                <h5>
                  <span className={`badge ${loadRoleColor(user.roleName)}`}>
                    {user.roleName}
                  </span>
                </h5>
                <span>{user.sites[0].name}</span>
                <span>{collectValue(user.name)}</span>
                <span>{collectValue(user.userName)}</span>
                <AccountStatus>{user.status}</AccountStatus>
                <TableDropDown>
                  {getDropDownItems(user)
                    .filter((dropDownItem) => dropDownItem.isVisible)
                    .map((item, index) => (
                      <Dropdown.Item
                        key={index}
                        bsPrefix="dropdown-item"
                        disabled={item.isDisabled}
                        onSelect={() => item.callBack(user)}
                      >
                        {item.text}
                      </Dropdown.Item>
                    ))}
                </TableDropDown>
              </Line>
            ))}
          </DefaultTable>
        </ProfileCard>
      </div>
    </div>
  );
};

const collectValue = (value) => {
  return value ? value : "-";
};

const loadRoleColor = (role) => {
  switch (role) {
    case profiles.studyAdmin:
      return "badge-study-admin";
    case profiles.itAdmin:
      return "badge-it-admin";
    case profiles.contentAdmin:
      return "badge-content-admin";
    default:
      break;
  }
};

const mapStateToProps = (state) => {
  return {
    ...mapStateToPropsLogin(state),
    ...mapStateToPropsManagement(state),
  };
};

export default connect(
  mapStateToProps,
  mapDispatchToPropsManagement
)(withTheme(AdminUsers));
